// components/AssetTestPage.tsx
import React, { useState, useEffect } from 'react';
import {
	Box,
	Container,
	Heading,
	VStack,
	HStack,
	Select,
	Button,
	Alert,
	AlertIcon,
	Text,
	Divider,
	Badge,
	Card,
	CardBody,
	CardHeader,
	Textarea,
	Input,
	FormControl,
	FormLabel,
	useToast,
	Spinner
} from '@chakra-ui/react';
import { PersonaAsset, AssetValidationResult, AssetType } from '../types';
import AssetServices from '../services/AssetServices';
import AssetValidator from './AssetValidator';

const AssetTestPage: React.FC = () => {
	const [selectedPersonaId, setSelectedPersonaId] = useState<string>('');
	const [selectedAssetId, setSelectedAssetId] = useState<string>('');
	const [assets, setAssets] = useState<PersonaAsset[]>([]);
	const [currentAsset, setCurrentAsset] = useState<PersonaAsset | null>(null);
	const [validationResults, setValidationResults] = useState<AssetValidationResult[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [testPersonaId, setTestPersonaId] = useState('test-persona-001');
	const [newAssetContent, setNewAssetContent] = useState('');
	const [newAssetName, setNewAssetName] = useState('');
	const [newAssetType, setNewAssetType] = useState<AssetType>('html');
	const toast = useToast();

	// Sample test assets for demonstration
	const sampleAssets: Record<AssetType, string> = {
		'html': `<div class="test-container">
	<h1>{{personaName}} Test Page</h1>
	<div class="content">
		{{htmlContent}}
	</div>
	<p>This is a sample HTML asset for testing.</p>
</div>`,
		'css': `.test-container {
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	padding: 20px;
	border-radius: 10px;
	color: white;
}

.test-container h1 {
	text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
	margin-bottom: 15px;
}

.content {
	background: rgba(255,255,255,0.1);
	padding: 15px;
	border-radius: 5px;
	margin: 10px 0;
}`,
		'javascript': `console.log('Asset test JavaScript executed');

// Sample interactive functionality
document.addEventListener('DOMContentLoaded', function() {
	const container = document.querySelector('.test-container');
	if (container) {
		container.addEventListener('click', function() {
			this.style.transform = this.style.transform === 'scale(1.05)' ? 'scale(1)' : 'scale(1.05)';
		});
	}
});`,
		'react-component': `const Component = ({ data = { title: "Test Component", items: ["Item 1", "Item 2", "Item 3"] } }) => {
	const [expanded, setExpanded] = useState(false);
	
	return (
		<div style={{
			border: '1px solid #ddd',
			borderRadius: '8px',
			padding: '16px',
			margin: '8px 0'
		}}>
			<h3 onClick={() => setExpanded(!expanded)} style={{ cursor: 'pointer' }}>
				{data.title} {expanded ? '▼' : '▶'}
			</h3>
			{expanded && (
				<ul style={{ marginTop: '10px' }}>
					{data.items.map((item, i) => (
						<li key={i}>{item}</li>
					))}
				</ul>
			)}
		</div>
	);
};`,
		'system-prompt-fragment': `You are an expert in the field related to this conversation. You have deep knowledge and can provide detailed, accurate information. When responding, consider the specific context and provide practical, actionable advice. Always maintain a helpful and professional tone.`,
		'media': `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8Y2lyY2xlIGN4PSI1MCIgY3k9IjUwIiByPSI0MCIgZmlsbD0iIzMzNzNkYyIgLz4KICA8dGV4dCB4PSI1MCIgeT0iNTUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlRlc3Q8L3RleHQ+Cjwvc3ZnPg==`
	};

	useEffect(() => {
		if (selectedPersonaId) {
			loadAssets();
		}
	}, [selectedPersonaId]);

	useEffect(() => {
		console.log('AssetTestPage useEffect triggered:', { selectedAssetId, assetsCount: assets.length });
		if (selectedAssetId && assets.length > 0) {
			const asset = assets.find(a => a.id === selectedAssetId);
			console.log('Found asset:', asset);
			setCurrentAsset(asset || null);
		} else {
			console.log('No asset selected or no assets available');
			setCurrentAsset(null);
		}
	}, [selectedAssetId, assets]);

	const loadAssets = async () => {
		if (!selectedPersonaId) return;

		setIsLoading(true);
		try {
			const response = await AssetServices.getAssets({ persona_id: selectedPersonaId });
			setAssets(response.assets);
			
			if (response.assets.length > 0) {
				setSelectedAssetId(response.assets[0].id);
			}
		} catch (error) {
			console.log('API not available, using mock assets for testing');
			
			// Create mock assets for testing when API is not available
			const mockAssets: PersonaAsset[] = [
				{
					id: 'mock-css-001',
					type: 'css',
					name: 'Sample CSS Theme',
					description: 'Mock CSS asset for testing',
					content: `.test-container {
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	padding: 20px;
	border-radius: 10px;
	color: white;
}`,
					metadata: '{"scope": "test"}',
					dependencies: '[]',
					tags: '["css", "theme"]',
					version: '1.0.0',
					created_at: Date.now(),
					updated_at: Date.now(),
					author: 'mock'
				},
				{
					id: 'mock-js-001',
					type: 'javascript',
					name: 'Sample JavaScript',
					description: 'Mock JavaScript asset for testing',
					content: 'console.log("Test JavaScript asset loaded");',
					metadata: '{}',
					dependencies: '[]',
					tags: '["javascript", "test"]',
					version: '1.0.0',
					created_at: Date.now(),
					updated_at: Date.now(),
					author: 'mock'
				},
				{
					id: 'mock-react-001',
					type: 'react-component',
					name: 'Sample React Component',
					description: 'Mock React component for testing',
					content: `const Component = ({ title = "Test Component" }) => {
	return <div style={{padding: '10px', border: '1px solid #ccc'}}>{title}</div>;
};`,
					metadata: '{"requires_react": true}',
					dependencies: '[]',
					tags: '["react", "component"]',
					version: '1.0.0',
					created_at: Date.now(),
					updated_at: Date.now(),
					author: 'mock'
				}
			];
			
			setAssets(mockAssets);
			if (mockAssets.length > 0) {
				setSelectedAssetId(mockAssets[0].id);
			}
			
			toast({
				title: 'Using mock assets',
				description: 'API not available, loaded sample assets for testing',
				status: 'info',
				duration: 3000,
				isClosable: true,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const createTestAsset = async () => {
		if (!testPersonaId || !newAssetName || !newAssetContent) {
			toast({
				title: 'Missing required fields',
				description: 'Please fill in persona ID, asset name, and content',
				status: 'warning',
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		setIsLoading(true);
		try {
			const newAsset = await AssetServices.createAsset({
				persona_id: testPersonaId,
				type: newAssetType,
				name: newAssetName,
				content: newAssetContent,
				description: `Test asset created from Asset Test Page`,
				version: '1.0.0',
				author: 'test-user'
			});

			toast({
				title: 'Asset created successfully',
				description: `Asset "${newAsset.name}" has been created`,
				status: 'success',
				duration: 3000,
				isClosable: true,
			});

			// Add to local assets list and select it
			setAssets(prev => [newAsset, ...prev]);
			setSelectedAssetId(newAsset.id);
			setCurrentAsset(newAsset);

			// Clear form
			setNewAssetName('');
			setNewAssetContent('');
		} catch (error) {
			toast({
				title: 'Error creating asset',
				description: error instanceof Error ? error.message : 'Unknown error',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const loadSampleAsset = (type: AssetType) => {
		setNewAssetType(type);
		setNewAssetContent(sampleAssets[type]);
		setNewAssetName(`Sample ${type} Asset`);
	};

	const handleValidationComplete = (result: AssetValidationResult) => {
		setValidationResults(prev => {
			const filtered = prev.filter(r => r.asset_id !== result.asset_id);
			return [result, ...filtered];
		});

		toast({
			title: result.is_valid ? 'Validation passed' : 'Validation failed',
			description: result.is_valid 
				? 'Asset is valid and ready for use' 
				: `${result.errors.length} issues found`,
			status: result.is_valid ? 'success' : 'error',
			duration: 3000,
			isClosable: true,
		});
	};

	return (
		<Container maxW="6xl" py={8}>
			<VStack spacing={6} align="stretch">
				<Heading size="xl" textAlign="center">
					Asset Test & Validation Page
				</Heading>

				{/* Asset Loading Section */}
				<Card>
					<CardHeader>
						<Heading size="md">Load Existing Assets</Heading>
					</CardHeader>
					<CardBody>
						<VStack spacing={4} align="stretch">
							<HStack>
								<FormControl>
									<FormLabel>Persona ID</FormLabel>
									<Input
										value={selectedPersonaId}
										onChange={(e) => setSelectedPersonaId(e.target.value)}
										placeholder="Enter persona ID to load assets"
									/>
								</FormControl>
								<Button onClick={loadAssets} isDisabled={!selectedPersonaId}>
									Load Assets
								</Button>
							</HStack>

							{assets.length > 0 && (
								<FormControl>
									<FormLabel>Select Asset</FormLabel>
									<Select value={selectedAssetId} onChange={(e) => setSelectedAssetId(e.target.value)}>
										{assets.map(asset => (
											<option key={asset.id} value={asset.id}>
												{asset.name} ({asset.type})
											</option>
										))}
									</Select>
								</FormControl>
							)}
						</VStack>
					</CardBody>
				</Card>

				{/* Asset Creation Section */}
				<Card>
					<CardHeader>
						<Heading size="md">Create Test Asset</Heading>
					</CardHeader>
					<CardBody>
						<VStack spacing={4} align="stretch">
							<HStack>
								<FormControl>
									<FormLabel>Test Persona ID</FormLabel>
									<Input
										value={testPersonaId}
										onChange={(e) => setTestPersonaId(e.target.value)}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Asset Type</FormLabel>
									<Select value={newAssetType} onChange={(e) => setNewAssetType(e.target.value as AssetType)}>
										<option value="html">HTML</option>
										<option value="css">CSS</option>
										<option value="javascript">JavaScript</option>
										<option value="react-component">React Component</option>
										<option value="system-prompt-fragment">System Prompt</option>
										<option value="media">Media</option>
									</Select>
								</FormControl>
							</HStack>

							<FormControl>
								<FormLabel>Asset Name</FormLabel>
								<Input
									value={newAssetName}
									onChange={(e) => setNewAssetName(e.target.value)}
									placeholder="Enter asset name"
								/>
							</FormControl>

							<FormControl>
								<FormLabel>Asset Content</FormLabel>
								<Textarea
									value={newAssetContent}
									onChange={(e) => setNewAssetContent(e.target.value)}
									placeholder="Enter asset content"
									minH="200px"
								/>
							</FormControl>

							<HStack>
								<Button onClick={createTestAsset} colorScheme="green" isLoading={isLoading}>
									Create Test Asset
								</Button>
								<Button onClick={() => loadSampleAsset(newAssetType)} variant="outline">
									Load Sample {newAssetType}
								</Button>
							</HStack>
						</VStack>
					</CardBody>
				</Card>

				{/* Asset Validation Section */}
				{currentAsset ? (
					<Card>
						<CardHeader>
							<HStack justifyContent="space-between">
								<Heading size="md">Asset Validation & Testing</Heading>
								<Badge colorScheme="blue">{currentAsset.type}</Badge>
							</HStack>
						</CardHeader>
						<CardBody>
							<Text fontSize="sm" mb={2} color="gray.600">
								Debug: Current asset ID: {currentAsset.id}
							</Text>
							<AssetValidator 
								asset={currentAsset} 
								onValidationComplete={handleValidationComplete}
							/>
						</CardBody>
					</Card>
				) : (
					<Alert status="info">
						<AlertIcon />
						<Text>
							{selectedAssetId ? 
								`No asset found with ID: ${selectedAssetId}` : 
								'Please select an asset to begin validation'
							}
						</Text>
					</Alert>
				)}

				{/* Validation Results Summary */}
				{validationResults.length > 0 && (
					<Card>
						<CardHeader>
							<Heading size="md">Validation History</Heading>
						</CardHeader>
						<CardBody>
							<VStack spacing={3} align="stretch">
								{validationResults.map((result, index) => (
									<Alert key={index} status={result.is_valid ? 'success' : 'error'}>
										<AlertIcon />
										<Box>
											<Text fontWeight="bold">
												Asset: {assets.find(a => a.id === result.asset_id)?.name || result.asset_id}
											</Text>
											<Text fontSize="sm">
												{result.is_valid ? 'Validation passed' : `${result.errors.length} errors found`}
											</Text>
											{!result.is_valid && (
												<Text fontSize="xs" mt={1} color="gray.600">
													{result.errors.slice(0, 2).join(', ')}
													{result.errors.length > 2 && '...'}
												</Text>
											)}
										</Box>
									</Alert>
								))}
							</VStack>
						</CardBody>
					</Card>
				)}

				{isLoading && (
					<Box textAlign="center" py={8}>
						<Spinner size="xl" />
						<Text mt={4}>Processing...</Text>
					</Box>
				)}
			</VStack>
		</Container>
	);
};

export default AssetTestPage;